import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../config';
import AddressCascade from '../AddressCascade';
import staffTopoFetcher from '../staffTopoFetcher';
import CiviciFtthCard from './CiviciFtthCard';
import { useAuth } from '../../auth';

/* Copertura dal sito TIM: la sala macchine dell'agente (stato, avanzamento, esiti) e la
   verifica di un singolo indirizzo. Il sito TIM non usa credenziali, quindi risponde
   anche quando SalesMate e' fermo. */

const nf = (n) => (n || 0).toLocaleString('it-IT');
const COL_ESITO = { 'COPERTO': '#22c55e', 'NON COPERTO': '#94a3b8', 'NON CENSITO': '#eab308', 'ERRORE': '#ef4444' };
const COL_TEC = { FTTH: '#14746f', FTTC: '#38bdf8', ADSL: '#3283bd', FWA: '#2dd4bf' };

function Barra({ label, value, total, color }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className="cop-bar-l"><span style={{ fontWeight: 600 }}>{label}</span><span className="cop-bar-n">{nf(value)} · {pct}%</span></div>
      <div className="cop-bar"><i style={{ width: `${pct}%`, background: color }} /></div>
    </div>
  );
}

const fmtVel = (v) => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!isFinite(n)) return String(v);
  return n >= 1000 ? `${(n / 1000).toFixed(n % 1000 ? 1 : 0)} Gbit/s` : `${n} Mbit/s`;
};
const colEsito = (e) => {
  const s = (e || '').toUpperCase();
  if (s.includes('NON CENS')) return '#94a3b8';
  if (s.includes('NON')) return '#ef4444';
  if (s.includes('COPERT') || s.includes('DISPONIB')) return '#22c55e';
  return '#3283bd';
};

export default function TabSitoTim() {
  const { user } = useAuth();
  const [dash, setDash] = useState(null);
  const [stato, setStato] = useState(null);
  const [busy, setBusy] = useState(false);
  // verifica singola
  const [addr, setAddr] = useState({ comune: '', via: '', civico: '' });
  const [ver, setVer] = useState(null);
  const [verBusy, setVerBusy] = useState(false);
  const vivo = useRef(true);

  const loadDash = useCallback(() => {
    axios.get(`${API}/copertura/dashboard`).then((r) => setDash(r.data)).catch(() => {});
  }, []);
  const loadStato = useCallback(() => {
    axios.get(`${API}/copertura/stato`).then((r) => setStato(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    vivo.current = true;
    loadDash(); loadStato();
    const t1 = setInterval(loadDash, 15000);
    const t2 = setInterval(loadStato, 3000);
    return () => { vivo.current = false; clearInterval(t1); clearInterval(t2); };
  }, [loadDash, loadStato]);

  const verifica = async () => {
    if (!addr.comune || !addr.via) return;
    setVerBusy(true); setVer(null);
    try {
      const r = await axios.post(`${API}/staff-ai/copertura-tim`, { comune: addr.comune, via: addr.via, civico: addr.civico });
      if (vivo.current) setVer(r.data || {});
    } catch (e) {
      if (vivo.current) setVer({ errore: e.response?.data?.detail || 'Errore verifica' });
    } finally { if (vivo.current) setVerBusy(false); }
  };

  if (!dash) return <div className="cop-empty">Caricamento…</div>;
  if (!dash.abilitata) {
    return (
      <div className="cop-card">
        <div className="cop-card-h">Verifica copertura disattivata</div>
        <p className="cop-note" style={{ margin: 0 }}>
          La verifica copertura TIM è spenta dall'amministratore (Impostazioni → Copertura TIM).
          Riattivala per usare l'agente e vedere le statistiche.
        </p>
      </div>
    );
  }

  const job = stato?.job;
  const inCorso = job && (job.status === 'running' || job.status === 'standby');
  const pct = job && job.total ? Math.round((job.completed / job.total) * 100) : 0;
  const per24h = (job?.velocita_min || 0) * 1440;
  const completamento = dash.verificabili ? Math.round((dash.verificati / dash.verificabili) * 100) : 0;
  const coperti = dash.per_esito['COPERTO'] || 0;
  const copertiPct = dash.verificati ? Math.round((coperti / dash.verificati) * 100) : 0;
  const totTec = Math.max(1, ['FTTH', 'FTTC', 'ADSL', 'FWA'].reduce((s, k) => s + (dash.per_tecnologia[k] || 0), 0));

  const toggleAgente = async (attivo) => {
    setBusy(true);
    try { await axios.post(`${API}/copertura/agente`, { attivo }); loadStato(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    setBusy(false);
  };
  const verificaMancanti = async () => {
    if (!window.confirm('Avviare la verifica copertura su TUTTI i numeri ancora da coprire?')) return;
    try {
      const r = await axios.post(`${API}/copertura-bulk`, { usa_filtro: true, filtro: {} });
      toast.success(`Avviata su ${r.data.total} numeri`);
      loadStato();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  return (
    <div className="cop">
      <div className="cop-card">
        <div className="cop-strip">
          <span className={`cop-dot ${stato?.agente_attivo ? 'on' : 'off'}`} />
          <b style={{ fontSize: 14.5 }}>Agente {stato?.agente_attivo ? 'attivo' : 'fermo'}</b>
          {stato?.in_attesa && <span className="cop-note">· in attesa{stato.motivo_attesa ? `: ${stato.motivo_attesa}` : ''}</span>}
          <div className="cop-spacer">
            <button className="btn btn-secondary" disabled={busy} onClick={verificaMancanti}>Verifica tutti i mancanti</button>
            <button className={`btn ${stato?.agente_attivo ? 'btn-secondary' : 'btn-primary'}`} disabled={busy}
              onClick={() => toggleAgente(!stato?.agente_attivo)}>
              {stato?.agente_attivo ? 'Ferma agente' : 'Avvia agente'}
            </button>
          </div>
        </div>
        <div className="cop-strip" style={{ marginTop: 12, gap: 22, fontSize: 13 }}>
          <div><span className="cop-note">Da verificare: </span><b>{nf(dash.pending)}</b></div>
          <div><span className="cop-note">Ritmo: </span><b>{job?.velocita_min || 0}/min</b></div>
          <div><span className="cop-note">Proiezione: </span><b>~{nf(per24h)}/24h</b></div>
        </div>
        {inCorso && (
          <div style={{ marginTop: 12 }}>
            <div className="cop-bar-l">
              <span>{job.agent ? 'Agente' : 'Lotto'} in corso — {nf(job.completed)}/{nf(job.total)} ({pct}%)
                {job.status === 'standby' && <b style={{ color: '#b45309' }}> · in pausa</b>}</span>
              <span className="cop-bar-n">{job.current_number ? `n. ${job.current_number}` : ''}</span>
            </div>
            <div className="cop-bar"><i style={{ width: `${pct}%`, background: '#3283bd' }} /></div>
          </div>
        )}
      </div>

      <div className="cop-kpis">
        <div className="cop-kpi"><div className="cop-kpi-v">{nf(dash.verificabili)}</div><div className="cop-kpi-l">Verificabili</div><div className="cop-kpi-s">indirizzo completo</div></div>
        <div className="cop-kpi"><div className="cop-kpi-v">{nf(dash.verificati)}</div><div className="cop-kpi-l">Verificati</div><div className="cop-kpi-s">{completamento}% del totale</div></div>
        <div className="cop-kpi"><div className="cop-kpi-v" style={{ color: '#16a34a' }}>{nf(coperti)}</div><div className="cop-kpi-l">Coperti TIM</div><div className="cop-kpi-s">{copertiPct}% dei verificati</div></div>
        <div className="cop-kpi"><div className="cop-kpi-v" style={{ color: '#b45309' }}>{nf(dash.pending)}</div><div className="cop-kpi-l">Da verificare</div></div>
      </div>

      <div className="cop-card">
        <div className="cop-card-h">Verifica un indirizzo</div>
        <div style={{ maxWidth: 560 }}>
          <AddressCascade variant="desktop" inputClass="topo-in" fetcher={staffTopoFetcher} rowLayout
            onChange={(a) => setAddr({ comune: a.comune, via: a.via, civico: a.civico })} />
        </div>
        <button className="btn btn-primary" style={{ marginTop: 10 }} onClick={verifica}
          disabled={verBusy || !addr.comune || !addr.via}>
          {verBusy ? 'Verifica in corso…' : 'Verifica dal sito TIM'}
        </button>
        {ver && (
          <div style={{ marginTop: 14 }}>
            {ver.errore ? <div style={{ color: '#ef4444', fontSize: 13 }}>{ver.errore}</div> : (
              <>
                <div style={{ display: 'inline-block', padding: '6px 14px', borderRadius: 10, fontWeight: 800, color: '#fff', background: colEsito(ver.esito) }}>
                  {ver.esito || '—'}{ver.tecnologia ? ` · ${ver.tecnologia}` : ''}
                </div>
                <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10, fontSize: 13 }}>
                  <div><div className="cop-note">Download</div><b>{fmtVel(ver.down)}</b></div>
                  <div><div className="cop-note">Upload</div><b>{fmtVel(ver.up)}</b></div>
                  <div><div className="cop-note">Latenza</div><b>{ver.latenza != null && ver.latenza !== '' ? `${ver.latenza} ms` : '—'}</b></div>
                  {ver.indirizzo_norm && <div><div className="cop-note">Indirizzo</div><b>{ver.indirizzo_norm}</b></div>}
                </div>
                {ver.tecnologia === 'FWA' && ver.down == null && ver.up == null && (
                  <div className="cop-note" style={{ marginTop: 8 }}>Per l'FWA il sito TIM non pubblica le velocità: mostra solo la latenza.</div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className="cop-cols">
        <div className="cop-card">
          <div className="cop-card-h">Ripartizione esiti</div>
          {['COPERTO', 'NON COPERTO', 'NON CENSITO', 'ERRORE'].map((k) => (
            <Barra key={k} label={k} value={dash.per_esito[k] || 0} total={dash.verificati} color={COL_ESITO[k]} />
          ))}
          {dash.verificati === 0 && <div className="cop-note">Nessun numero ancora verificato.</div>}
        </div>
        <div className="cop-card">
          <div className="cop-card-h">Tecnologie trovate</div>
          {['FTTH', 'FTTC', 'ADSL', 'FWA'].map((k) => (
            <Barra key={k} label={k} value={dash.per_tecnologia[k] || 0} total={totTec} color={COL_TEC[k]} />
          ))}
        </div>
      </div>

      {/* L'acceleratore: gli elenchi che TIM consegna coprono i clienti senza verifiche.
          Riscrive la copertura dei record, quindi resta dell'amministratore. */}
      {user?.ruolo === 'admin' && <CiviciFtthCard />}

      <div className="cop-cols">
        {dash.indirizzi && (
          <div className="cop-card">
            <div className="cop-card-h">Completezza indirizzi</div>
            <p className="cop-note" style={{ margin: '0 0 10px' }}>
              L'agente verifica <b>solo</b> gli indirizzi completi (comune, via e civico, SNC incluso):
              gli altri darebbero NON CENSITO e verrebbero ripescati a ogni giro.
            </p>
            <Barra label="Completo — verificabile" value={dash.indirizzi.completo} total={dash.indirizzi.totale} color="#22c55e" />
            <Barra label="Manca il civico" value={dash.indirizzi.manca_civico} total={dash.indirizzi.totale} color="#eab308" />
            <Barra label="Manca la via" value={dash.indirizzi.manca_via} total={dash.indirizzi.totale} color="#f97316" />
            <Barra label="Senza indirizzo" value={dash.indirizzi.senza_indirizzo} total={dash.indirizzi.totale} color="#94a3b8" />
          </div>
        )}
        <div className="cop-card">
          <div className="cop-card-h">Velocità media dei coperti</div>
          <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap' }}>
            <div><div className="cop-kpi-v">{dash.medie.down ? nf(dash.medie.down) : '—'}</div><div className="cop-kpi-l">Download Mbit/s</div></div>
            <div><div className="cop-kpi-v">{dash.medie.up ? nf(dash.medie.up) : '—'}</div><div className="cop-kpi-l">Upload Mbit/s</div></div>
            <div><div className="cop-kpi-v">{dash.medie.latenza != null ? dash.medie.latenza : '—'}</div><div className="cop-kpi-l">Latenza ms</div></div>
          </div>
          <div style={{ marginTop: 18 }}>
            <div className="cop-bar-l"><span style={{ fontWeight: 600 }}>Avanzamento archivio</span><span className="cop-bar-n">{nf(dash.verificati)} / {nf(dash.verificabili)} · {completamento}%</span></div>
            <div className="cop-bar" style={{ height: 12 }}><i style={{ width: `${completamento}%`, background: 'linear-gradient(90deg,#3283bd,#22c55e)' }} /></div>
          </div>
        </div>
      </div>

      <div className="cop-card" style={{ padding: 0 }}>
        <div className="cop-card-h" style={{ padding: '14px 16px 0', margin: 0 }}>Ultimi lotti</div>
        {(!dash.storico || dash.storico.length === 0) ? (
          <div className="cop-empty">Nessun lotto ancora eseguito.</div>
        ) : (
          <div className="cop-tablewrap" style={{ border: 0, marginTop: 10 }}>
            <table className="cop-table">
              <thead>
                <tr>
                  <th className="l">Data</th><th className="l">Tipo</th><th className="l">Stato</th>
                  <th>Fatti</th><th>Coperti</th><th>Non cop.</th><th>Non cens.</th><th>Errori</th><th>Saltati</th>
                </tr>
              </thead>
              <tbody>
                {dash.storico.map((j) => (
                  <tr key={j.id}>
                    <td className="l">{j.created_at ? new Date(j.created_at).toLocaleString('it-IT') : '—'}</td>
                    <td className="l">{j.agent ? 'Agente' : 'Lotto'}</td>
                    <td className="l">{j.status}</td>
                    <td>{nf(j.completed)}/{nf(j.total)}</td>
                    <td style={{ color: '#16a34a' }}>{nf(j.coperto)}</td>
                    <td>{nf(j.non_coperto)}</td>
                    <td style={{ color: '#b45309' }}>{nf(j.non_censito)}</td>
                    <td style={{ color: '#ef4444' }}>{nf(j.errore)}</td>
                    <td>{nf(j.skipped)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
